import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { visitorsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UserPlus,
  QrCode,
  Clock,
  Car,
  User,
  Phone,
  Calendar as CalendarIcon,
  Check,
  Loader2,
  Copy,
  ChevronRight,
  LogIn,
  LogOut,
} from 'lucide-react';

export default function VisitorManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [visitors, setVisitors] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'register' | 'qr'
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [newVisitor, setNewVisitor] = useState({
    visitor_name: '',
    visitor_phone: '',
    purpose: '',
    vehicle_number: '',
  });
  const [generatedQR, setGeneratedQR] = useState(null);

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const data = await visitorsApi.list(hoaId).catch(() => getDemoVisitors());
      setVisitors(data);
    } catch (error) {
      setVisitors(getDemoVisitors());
    } finally {
      setLoading(false);
    }
  };

  const getDemoVisitors = () => [
    { visitor_id: 'vis_001', visitor_name: 'John Smith', visitor_phone: '+1 555-0123', purpose: 'Delivery', qr_code: 'QR123ABC456', expected_date: new Date().toISOString(), check_in_time: null, check_out_time: null },
    { visitor_id: 'vis_002', visitor_name: 'Sarah Johnson', visitor_phone: '+1 555-0456', purpose: 'Family Visit', qr_code: 'QR789DEF012', expected_date: new Date(Date.now() + 86400000).toISOString(), check_in_time: null, check_out_time: null },
    { visitor_id: 'vis_003', visitor_name: 'Mike Davis', visitor_phone: '+1 555-0789', purpose: 'Contractor', qr_code: 'QR345GHI678', expected_date: new Date(Date.now() - 86400000).toISOString(), check_in_time: new Date(Date.now() - 86400000 + 3600000).toISOString(), check_out_time: new Date(Date.now() - 86400000 + 14400000).toISOString() },
  ];

  const handleRegister = async () => {
    if (!newVisitor.visitor_name || !newVisitor.purpose) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const result = await visitorsApi.register({
        hoa_id: hoaId,
        unit_id: user?.unit_id || 'unit_001',
        visitor_name: newVisitor.visitor_name,
        visitor_phone: newVisitor.visitor_phone,
        purpose: newVisitor.purpose,
        expected_date: selectedDate.toISOString(),
      });

      setGeneratedQR(result.qr_code);
      setView('qr');
      toast.success('Visitor registered!');
      fetchVisitors();
    } catch (error) {
      // Demo fallback
      const demoQR = `QR${Date.now().toString(36).toUpperCase()}`;
      setGeneratedQR(demoQR);
      setView('qr');
      toast.success('Visitor registered! (Demo)');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyQR = () => {
    navigator.clipboard.writeText(generatedQR);
    toast.success('QR code copied!');
  };

  const handleCancel = async (visitorId) => {
    try {
      await visitorsApi.delete(visitorId);
      toast.success('Visitor registration cancelled');
      fetchVisitors();
    } catch (error) {
      setVisitors(visitors.filter(v => v.visitor_id !== visitorId));
      toast.success('Registration cancelled');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getVisitorStatus = (visitor) => {
    if (visitor.check_out_time) return { label: 'Completed', color: 'bg-slate-100 text-slate-600' };
    if (visitor.check_in_time) return { label: 'Checked In', color: 'bg-emerald-100 text-emerald-700' };
    const expectedDate = new Date(visitor.expected_date);
    const today = new Date();
    if (expectedDate.toDateString() === today.toDateString()) return { label: 'Expected Today', color: 'bg-sky-100 text-sky-700' };
    if (expectedDate > today) return { label: 'Upcoming', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Expired', color: 'bg-red-100 text-red-700' };
  };

  if (view === 'qr' && generatedQR) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setView('list'); setGeneratedQR(null); setNewVisitor({ visitor_name: '', visitor_phone: '', purpose: '', vehicle_number: '' }); }}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Visitor Pass</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6">
          <Card className="text-center" data-testid="qr-card">
            <CardContent className="p-8">
              <div className="w-48 h-48 bg-slate-900 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <QrCode className="w-32 h-32 text-white" />
              </div>
              <h2 className="text-xl font-bold font-manrope text-slate-900 mb-2">
                {newVisitor.visitor_name}
              </h2>
              <p className="text-slate-500 mb-4">{newVisitor.purpose}</p>
              
              <div className="bg-slate-100 rounded-xl p-4 mb-6">
                <p className="text-xs text-slate-500 mb-1">Access Code</p>
                <p className="text-2xl font-mono font-bold text-slate-900">{generatedQR}</p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleCopyQR}
                  data-testid="copy-qr-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </Button>
                <Button 
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={() => toast.success('Pass shared!')}
                  data-testid="share-btn"
                >
                  Share Pass
                </Button>
              </div>

              <p className="text-xs text-slate-400 mt-4">
                Show this code to security at the gate
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (view === 'register') {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Register Visitor</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Visitor Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Full name"
                  value={newVisitor.visitor_name}
                  onChange={(e) => setNewVisitor({ ...newVisitor, visitor_name: e.target.value })}
                  className="pl-10 h-12"
                  data-testid="visitor-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="+1 555-0000"
                  value={newVisitor.visitor_phone}
                  onChange={(e) => setNewVisitor({ ...newVisitor, visitor_phone: e.target.value })}
                  className="pl-10 h-12"
                  data-testid="visitor-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Purpose of Visit *</label>
              <Input
                placeholder="e.g., Delivery, Family Visit, Contractor"
                value={newVisitor.purpose}
                onChange={(e) => setNewVisitor({ ...newVisitor, purpose: e.target.value })}
                className="h-12"
                data-testid="purpose-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Vehicle Number (Optional)</label>
              <div className="relative">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="License plate"
                  value={newVisitor.vehicle_number}
                  onChange={(e) => setNewVisitor({ ...newVisitor, vehicle_number: e.target.value })}
                  className="pl-10 h-12"
                  data-testid="vehicle-input"
                />
              </div>
            </div>
          </div>

          <Card data-testid="date-picker-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-manrope flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-sky-600" />
                Expected Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                className="rounded-md border-0"
              />
            </CardContent>
          </Card>

          <Button 
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
            onClick={handleRegister}
            disabled={submitting}
            data-testid="register-visitor-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <QrCode className="w-5 h-5 mr-2" />
                Generate Visitor Pass
              </>
            )}
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Visitors</h1>
          </div>
          <Button 
            onClick={() => setView('register')}
            className="rounded-full bg-slate-900 hover:bg-slate-800"
            data-testid="register-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Register
          </Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <>
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </>
        ) : visitors.length > 0 ? (
          visitors.map((visitor, index) => {
            const status = getVisitorStatus(visitor);
            return (
              <Card 
                key={visitor.visitor_id}
                className="overflow-hidden animate-slideUp"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`visitor-${visitor.visitor_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{visitor.visitor_name}</h3>
                        <p className="text-sm text-slate-500">{visitor.purpose}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={status.color}>{status.label}</Badge>
                          <span className="text-xs text-slate-400">{formatDate(visitor.expected_date)}</span>
                        </div>
                        {visitor.check_in_time && (
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <LogIn className="w-3 h-3" />
                              {formatTime(visitor.check_in_time)}
                            </span>
                            {visitor.check_out_time && (
                              <span className="flex items-center gap-1">
                                <LogOut className="w-3 h-3" />
                                {formatTime(visitor.check_out_time)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {!visitor.check_in_time && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600"
                        onClick={() => handleCancel(visitor.visitor_id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <QrCode className="w-4 h-4" />
                      <span className="font-mono">{visitor.qr_code}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="text-sky-600" onClick={() => { setGeneratedQR(visitor.qr_code); setNewVisitor({ visitor_name: visitor.visitor_name, purpose: visitor.purpose, visitor_phone: visitor.visitor_phone || '', vehicle_number: '' }); setView('qr'); }}>
                      View Pass
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">No visitors</h3>
              <p className="text-slate-500 text-sm mb-4">Register your first visitor</p>
              <Button onClick={() => setView('register')} className="rounded-full bg-slate-900 hover:bg-slate-800">
                <UserPlus className="w-4 h-4 mr-1" />
                Register Visitor
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
